import React, { useState, useEffect } from 'react'
import toast from 'react-hot-toast'
import AdminLogin from '../components/AdminLogin'
import BlogManagement from '../components/admin/BlogManagement'
import authService from '../services/authService'

interface Order {
  _id: string
  orderNumber: string
  customerInfo: {
    firstName: string
    lastName: string
    email: string
    phone: string
  }
  items: Array<{
    title: string
    isExpress: boolean
    quantity: number
    price: number
  }>
  status: string
  totalAmount: number
  trackingNumber?: string
  notes?: string
  statusHistory?: Array<{
    status: string
    message?: string
    updatedAt: string
    updatedBy?: string
  }>
  createdAt: string
  updatedAt: string
}

interface StatusDefinitions {
  [key: string]: string
}

const AdminPage: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [activeTab, setActiveTab] = useState<'orders' | 'blog'>('orders')
  const [orders, setOrders] = useState<Order[]>([])
  const [statusDefinitions, setStatusDefinitions] = useState<StatusDefinitions>({})
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [newStatus, setNewStatus] = useState('')
  const [statusMessage, setStatusMessage] = useState('')
  const [trackingNumber, setTrackingNumber] = useState('')
  const [loading, setLoading] = useState(true)

  const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:5000/api'

  useEffect(() => {
    // Check authentication on component mount
    checkAuthentication()
  }, [])

  const checkAuthentication = async () => {
    if (authService.isAuthenticated()) {
      try {
        const verification = await authService.verifyToken()
        if (verification.success) {
          setIsAuthenticated(true)
          await fetchOrders()
          await fetchStatusDefinitions()
        } else {
          authService.logout()
          setIsAuthenticated(false)
        }
      } catch (error) {
        authService.logout()
        setIsAuthenticated(false)
        toast.error('Authentifizierung fehlgeschlagen')
      }
    }
    setLoading(false)
  }

  const fetchOrders = async () => {
    try {
      const response = await fetch(`${apiUrl}/admin/orders`, {
        headers: {
          'Content-Type': 'application/json',
          ...authService.getAuthHeader()
        }
      })

      const data = await response.json()

      if (response.status === 401) {
        authService.logout()
        setIsAuthenticated(false)
        toast.error('Session abgelaufen. Bitte erneut anmelden.')
        return
      }

      if (data.success) {
        setOrders(data.data)
      } else {
        toast.error(data.error || 'Fehler beim Laden der Bestellungen')
      }
    } catch (error) {
      console.error('Error fetching orders:', error)
      toast.error('Fehler beim Laden der Bestellungen')
    }
  }

  const fetchStatusDefinitions = async () => {
    try {
      const response = await fetch(`${apiUrl}/admin/status-definitions`, {
        headers: {
          'Content-Type': 'application/json',
          ...authService.getAuthHeader()
        }
      })

      const data = await response.json()

      if (response.status === 401) {
        authService.logout()
        setIsAuthenticated(false)
        return
      }

      if (data.success) {
        setStatusDefinitions(data.data)
      }
    } catch (error) {
      console.error('Error fetching status definitions:', error)
    }
  }

  const updateOrderStatus = async () => {
    if (!selectedOrder || !newStatus) {
      toast.error('Bitte alle Felder ausfüllen')
      return
    }

    try {
      const updateData: any = {
        status: newStatus
      }

      if (statusMessage) {
        updateData.message = statusMessage
      }

      if (trackingNumber) {
        updateData.trackingNumber = trackingNumber
      }

      const response = await fetch(`${apiUrl}/admin/orders/${selectedOrder.orderNumber}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...authService.getAuthHeader()
        },
        body: JSON.stringify(updateData)
      })

      const data = await response.json()

      if (response.status === 401) {
        authService.logout()
        setIsAuthenticated(false)
        toast.error('Session abgelaufen. Bitte erneut anmelden.')
        return
      }

      if (data.success) {
        toast.success('Status erfolgreich aktualisiert')
        await fetchOrders()
        setSelectedOrder(null)
        setNewStatus('')
        setStatusMessage('')
        setTrackingNumber('')
      } else {
        toast.error(data.error || 'Fehler beim Aktualisieren des Status')
      }
    } catch (error) {
      console.error('Error updating order status:', error)
      toast.error('Fehler beim Aktualisieren des Status')
    }
  }

  const handleLogin = () => {
    setIsAuthenticated(true)
    setLoading(true)
    checkAuthentication()
  }

  const handleLogout = () => {
    authService.logout()
    setIsAuthenticated(false)
    setOrders([])
    setSelectedOrder(null)
    toast.success('Erfolgreich abgemeldet')
  }

  const filteredOrders = orders.filter(order =>
    order.orderNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
    order.customerInfo.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    `${order.customerInfo.firstName} ${order.customerInfo.lastName}`.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('de-DE')
  }

  const formatPrice = (price: number) => {
    return `${price.toFixed(2)}€`
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-indigo-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Lade Admin-Dashboard...</p>
        </div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return <AdminLogin onLogin={handleLogin} />
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
              <p className="text-sm text-gray-500">PhoneMedic Verwaltung</p>
            </div>
            <button
              onClick={handleLogout}
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg transition-colors"
            >
              Abmelden
            </button>
          </div>

          {/* Tab Navigation */}
          <div className="flex space-x-1 border-b border-gray-200 -mb-px">
            <button
              onClick={() => setActiveTab('orders')}
              className={`px-6 py-3 font-medium text-sm transition-colors ${
                activeTab === 'orders'
                  ? 'border-b-2 border-indigo-600 text-indigo-600'
                  : 'text-gray-600 hover:text-gray-900 hover:border-gray-300'
              }`}
            >
              Bestellungen
            </button>
            <button
              onClick={() => setActiveTab('blog')}
              className={`px-6 py-3 font-medium text-sm transition-colors ${
                activeTab === 'blog'
                  ? 'border-b-2 border-indigo-600 text-indigo-600'
                  : 'text-gray-600 hover:text-gray-900 hover:border-gray-300'
              }`}
            >
              Blog
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Orders Tab */}
        {activeTab === 'orders' && (
          <>
            {/* Search and Stats */}
            <div className="mb-8">
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="flex-1">
              <input
                type="text"
                placeholder="Suche nach Bestellnummer, E-Mail oder Name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>
            <button
              onClick={fetchOrders}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors whitespace-nowrap"
            >
              🔄 Aktualisieren
            </button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="text-lg font-medium text-gray-900">Gesamt Bestellungen</h3>
              <p className="text-3xl font-bold text-blue-600">{orders.length}</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="text-lg font-medium text-gray-900">Offene Bestellungen</h3>
              <p className="text-3xl font-bold text-orange-600">
                {orders.filter(order => !['completed', 'cancelled'].includes(order.status)).length}
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="text-lg font-medium text-gray-900">Abgeschlossene Bestellungen</h3>
              <p className="text-3xl font-bold text-green-600">
                {orders.filter(order => order.status === 'completed').length}
              </p>
            </div>
          </div>
        </div>

        {/* Orders Table */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Bestellnummer
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Kunde
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Betrag
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Erstellt
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Aktionen
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredOrders.map((order) => (
                  <tr key={order._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{order.orderNumber}</div>
                      {order.trackingNumber && (
                        <div className="text-sm text-gray-500">Tracking: {order.trackingNumber}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {order.customerInfo.firstName} {order.customerInfo.lastName}
                      </div>
                      <div className="text-sm text-gray-500">{order.customerInfo.email}</div>
                      <div className="text-sm text-gray-500">{order.customerInfo.phone}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                        {statusDefinitions[order.status] || order.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatPrice(order.totalAmount)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(order.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => setSelectedOrder(order)}
                        className="text-indigo-600 hover:text-indigo-900"
                      >
                        Status ändern
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredOrders.length === 0 && (
            <div className="text-center py-8">
              <p className="text-gray-500">Keine Bestellungen gefunden</p>
            </div>
          )}
        </div>
          </>
        )}

        {/* Blog Tab */}
        {activeTab === 'blog' && (
          <BlogManagement />
        )}
      </div>

      {/* Status Update Modal */}
      {selectedOrder && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Status aktualisieren - {selectedOrder.orderNumber}
              </h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Neuer Status
                  </label>
                  <select
                    value={newStatus}
                    onChange={(e) => setNewStatus(e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  >
                    <option value="">Status wählen...</option>
                    {Object.entries(statusDefinitions).map(([key, value]) => (
                      <option key={key} value={key}>{value}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nachricht (optional)
                  </label>
                  <textarea
                    value={statusMessage}
                    onChange={(e) => setStatusMessage(e.target.value)}
                    placeholder="Zusätzliche Informationen für den Kunden..."
                    rows={3}
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tracking-Nummer (optional)
                  </label>
                  <input
                    type="text"
                    value={trackingNumber}
                    onChange={(e) => setTrackingNumber(e.target.value)}
                    placeholder="DHL Tracking-Nummer"
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>

                {/* Order Details */}
                <div className="bg-gray-50 p-4 rounded-md">
                  <h4 className="font-medium text-gray-900 mb-2">Bestelldetails:</h4>
                  <div className="space-y-1 text-sm text-gray-600">
                    {selectedOrder.items.map((item, index) => (
                      <div key={index} className="flex justify-between">
                        <span>{item.title} {item.isExpress && '(Express)'}</span>
                        <span>{formatPrice(item.price)}</span>
                      </div>
                    ))}
                    <div className="border-t pt-1 font-medium">
                      <div className="flex justify-between">
                        <span>Gesamt:</span>
                        <span>{formatPrice(selectedOrder.totalAmount)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Status History */}
                {selectedOrder.statusHistory && selectedOrder.statusHistory.length > 0 && (
                  <div className="bg-gray-50 p-4 rounded-md">
                    <h4 className="font-medium text-gray-900 mb-2">Status-Verlauf:</h4>
                    <div className="space-y-2 text-sm">
                      {selectedOrder.statusHistory.map((entry, index) => (
                        <div key={index} className="flex justify-between items-start">
                          <div>
                            <span className="font-medium">{statusDefinitions[entry.status] || entry.status}</span>
                            {entry.message && (
                              <div className="text-gray-600 mt-1">{entry.message}</div>
                            )}
                          </div>
                          <span className="text-gray-500 text-xs whitespace-nowrap ml-2">
                            {formatDate(entry.updatedAt)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => {
                    setSelectedOrder(null)
                    setNewStatus('')
                    setStatusMessage('')
                    setTrackingNumber('')
                  }}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
                >
                  Abbrechen
                </button>
                <button
                  onClick={updateOrderStatus}
                  disabled={!newStatus}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                >
                  Status aktualisieren
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AdminPage